import React from 'react';
import {
  FaAws, FaBrain, FaBug, FaCss3Alt, FaCubes, FaDatabase,
  FaDocker, FaGamepad, FaGitAlt, FaGithub, FaHtml5, FaJava,
  FaJs, FaLink, FaLinux, FaLock, FaMicrophone, FaMobileAlt,
  FaNetworkWired, FaNodeJs, FaPython, FaReact, FaRobot, FaSearch,
  FaUserSecret
} from 'react-icons/fa';
import {
  SiBabylondotjs, SiBlender, SiCsharp, SiExpress, SiFastapi, SiFigma,
  SiFirebase, SiFlutter, SiGithubactions, SiGodotengine, SiGraphql, SiMicrosoftazure,
  SiMongodb, SiNestjs, SiNextdotjs, SiOpenai, SiPostgresql, SiPrisma,
  SiReact, SiTailwindcss, SiThreedotjs, SiTypescript, SiUnity, SiUnrealengine,
  SiWebpack, SiWireshark
} from 'react-icons/si';
import {
  MdArchitecture, MdSecurity
} from 'react-icons/md';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

interface Skill {
  name: string;
  icon: React.ReactNode;
  category: 'ai' | 'frontend' | 'backend' | 'gamedev' | 'security' | 'tools';
}

// Six modules in the site-wide order: AI → Full-Stack (frontend/mobile, backend) → Game Dev → Security → Tooling
const skills: Skill[] = [
  // Artificial Intelligence
  { name: 'RAG', icon: <FaSearch className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'ai' },
  { name: 'LangChain', icon: <FaLink className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'ai' },
  { name: 'LangGraph', icon: <FaNetworkWired className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'ai' },
  { name: 'Langfuse', icon: <FaRobot className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'ai' },
  { name: 'OpenAI API', icon: <SiOpenai className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'ai' },
  { name: 'Multi-Agent', icon: <FaCubes className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'ai' },
  { name: 'Whisper ASR', icon: <FaMicrophone className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'ai' },
  { name: 'NLP', icon: <FaBrain className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'ai' },
  { name: 'LLM Eval', icon: <FaRobot className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'ai' },

  // Frontend & Mobile
  { name: 'React.js', icon: <FaReact className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'frontend' },
  { name: 'Next.js', icon: <SiNextdotjs className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'frontend' },
  { name: 'TypeScript', icon: <SiTypescript className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'frontend' },
  { name: 'JavaScript', icon: <FaJs className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'frontend' },
  { name: 'TailwindCSS', icon: <SiTailwindcss className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'frontend' },
  { name: 'HTML', icon: <FaHtml5 className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'frontend' },
  { name: 'CSS', icon: <FaCss3Alt className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'frontend' },
  { name: 'Flutter', icon: <SiFlutter className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'frontend' },
  { name: 'React Native', icon: <SiReact className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'frontend' },

  // Backend & Data
  { name: 'Node.js', icon: <FaNodeJs className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'backend' },
  { name: 'NestJS', icon: <SiNestjs className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'backend' },
  { name: 'Express', icon: <SiExpress className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'backend' },
  { name: 'GraphQL', icon: <SiGraphql className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'backend' },
  { name: 'Python', icon: <FaPython className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'backend' },
  { name: 'FastAPI', icon: <SiFastapi className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'backend' },
  { name: 'Java', icon: <FaJava className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'backend' },
  { name: 'PostgreSQL', icon: <SiPostgresql className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'backend' },
  { name: 'MySQL', icon: <FaDatabase className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'backend' },
  { name: 'MongoDB', icon: <SiMongodb className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'backend' },
  { name: 'Prisma', icon: <SiPrisma className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'backend' },

  // Game Development
  { name: 'Unity', icon: <SiUnity className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'gamedev' },
  { name: 'Unreal Engine', icon: <SiUnrealengine className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'gamedev' },
  { name: 'Godot', icon: <SiGodotengine className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'gamedev' },
  { name: 'C#', icon: <SiCsharp className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'gamedev' },
  { name: 'Blender', icon: <SiBlender className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'gamedev' },
  { name: 'Three.js', icon: <SiThreedotjs className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'gamedev' },
  { name: 'Babylon.js', icon: <SiBabylondotjs className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'gamedev' },
  { name: 'React Three Fiber', icon: <SiReact className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'gamedev' },
  { name: 'Game Physics', icon: <FaGamepad className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'gamedev' },
  { name: 'Multiplayer', icon: <FaNetworkWired className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'gamedev' },

  // Cybersecurity
  { name: 'Pentesting', icon: <MdSecurity className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'security' },
  { name: 'Burp Suite', icon: <FaBug className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'security' },
  { name: 'OWASP ZAP', icon: <FaUserSecret className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'security' },
  { name: 'MobSF', icon: <FaMobileAlt className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'security' },
  { name: 'Frida', icon: <FaBug className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'security' },
  { name: 'Wireshark', icon: <SiWireshark className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'security' },
  { name: 'Cryptography', icon: <FaLock className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'security' },
  { name: 'Web Testing', icon: <SiWebpack className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'security' },
  { name: 'Android Testing', icon: <MdSecurity className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'security' },

  // DevOps & Architecture
  { name: 'Docker', icon: <FaDocker className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'tools' },
  { name: 'Git', icon: <FaGitAlt className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'tools' },
  { name: 'GitHub', icon: <FaGithub className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'tools' },
  { name: 'CI/CD', icon: <SiGithubactions className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'tools' },
  { name: 'Linux', icon: <FaLinux className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'tools' },
  { name: 'AWS', icon: <FaAws className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'tools' },
  { name: 'Azure', icon: <SiMicrosoftazure className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'tools' },
  { name: 'Firebase', icon: <SiFirebase className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'tools' },
  { name: 'Figma', icon: <SiFigma className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'tools' },
  { name: 'Microservices', icon: <MdArchitecture className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'tools' },
  { name: 'Monolithic', icon: <MdArchitecture className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8" />, category: 'tools' },
];

const modernCategoryColors: Record<string, string> = {
  ai: 'modern-card-teal',
  frontend: 'modern-card-blue',
  backend: 'modern-card-indigo',
  gamedev: 'modern-card-green',
  security: 'modern-card-red',
  tools: 'modern-card-orange'
};

const categoryOrder: Skill['category'][] = ['ai', 'frontend', 'backend', 'gamedev', 'security', 'tools'];

// Stencil banner color rotation for neobrutalism patches
const nbBannerColors = ['nb-mod--yellow', 'nb-mod--blue', 'nb-mod--green', 'nb-mod--pink', 'nb-mod--orange', 'nb-mod--purple', 'nb-mod--red'];

const SkillsGrid: React.FC = () => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const isModern = theme === 'modern';

  // Group skills by category, preserving the configured order
  const groupedSkills = categoryOrder
    .map((cat) => ({ category: cat, items: skills.filter((s) => s.category === cat) }))
    .filter((g) => g.items.length > 0);

  const totalSubsystems = skills.length;

  if (isModern) {
    /* SCI-FI — SYSTEM CAPABILITY MATRIX */
    return (
      <div className="sf-modules">
        {/* Counter strip */}
        <div className="sf-modules__counter">
          <span>TOTAL_MODULES <strong>{String(groupedSkills.length).padStart(2, '0')}</strong></span>
          <span className="sf-modules__counter-bar"></span>
          <span>SUBSYSTEMS <strong>{String(totalSubsystems).padStart(2, '0')}</strong></span>
          <span className="sf-modules__counter-bar"></span>
          <span className="sf-modules__counter-status">● ALL READY</span>
        </div>

        <div className="sf-modules__grid">
          {groupedSkills.map(({ category, items }, idx) => (
            <article key={category} className={`sf-mod modern-card ${modernCategoryColors[category]}`}>
              {/* Module header */}
              <div className="sf-mod__head">
                <span className="sf-mod__code">MOD_{String(idx + 1).padStart(2, '0')}</span>
                <span className="sf-mod__sep">::</span>
                <span className="sf-mod__name">{t(`skills.categories.${category}`)}</span>
                <span className="sf-mod__status">● ACTIVE</span>
              </div>

              {/* Subsystem chips */}
              <ul className="sf-mod__list">
                {items.map((skill) => (
                  <li key={skill.name} className="sf-mod__chip">
                    <span className="sf-mod__chip-icon">{skill.icon}</span>
                    <span className="sf-mod__chip-name">{skill.name}</span>
                    <span className="sf-mod__chip-dot" aria-hidden="true"></span>
                  </li>
                ))}
              </ul>

              {/* Footer load bar */}
              <div className="sf-mod__foot">
                <span className="sf-mod__foot-bar"><span></span></span>
                <span className="sf-mod__foot-meta">{String(items.length).padStart(2, '0')}/{String(items.length).padStart(2, '0')} LOADED</span>
              </div>
            </article>
          ))}
        </div>
      </div>
    );
  }

  /* NEOBRUTALISM — SKILL PATCH ARSENAL */
  return (
    <div className="nb-arsenal">
      <div className="nb-arsenal__counter">
        <span>★ {groupedSkills.length} MODULES</span>
        <span>★ {totalSubsystems} PATCHES</span>
        <span>★ ALL STAMPED</span>
      </div>

      <div className="nb-arsenal__grid">
        {groupedSkills.map(({ category, items }, idx) => (
          <article key={category} className={`nb-mod ${nbBannerColors[idx % nbBannerColors.length]}`}>
            <div className="nb-mod__banner">
              <span className="nb-mod__banner-code">PATCH_{String(idx + 1).padStart(2, '0')}</span>
              <span className="nb-mod__banner-name">{t(`skills.categories.${category}`)}</span>
            </div>
            <span className="nb-mod__count">★ {items.length}</span>

            <ul className="nb-mod__list">
              {items.map((skill, i) => (
                <li key={skill.name} className={`nb-mod__chip nb-mod__chip--${i % 4}`}>
                  <span className="nb-mod__chip-icon">{skill.icon}</span>
                  <span className="nb-mod__chip-name">{skill.name}</span>
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </div>
  );
};

export default SkillsGrid; 